package Structs.Api

import Utils.getJsonWithConfig
import io.ktor.http.encodeURLParameter
import kotlinx.serialization.Serializable
import kotlinx.serialization.json.JsonArray
import kotlinx.serialization.json.JsonElement
import kotlinx.serialization.json.JsonObject
import kotlinx.serialization.json.JsonPrimitive
import kotlinx.serialization.json.booleanOrNull
import kotlinx.serialization.json.contentOrNull
import kotlinx.serialization.json.doubleOrNull
import kotlinx.serialization.json.intOrNull

/**
 * AI-assistant drafts API DTOs + per-company config resolver.
 *
 * The AI backend is a JSON REST surface ("/v1/drafts...") authed with a
 * company `nk_live_...` bearer that lives in [ApiConfig.ai]. These types mirror
 * the web client (ai_assistent/frontend api/client.ts) closely enough for the
 * Android draft list + compare + approve flow.
 */

/** Resolved (apiBase, token, phone) for the current company/agent. */
data class AiDraftConfig(
    val apiBase: String,
    val token: String,
    /** When true, Android may expose AI draft entry points (e.g. orders FAB). */
    val phone: Boolean = false,
) {
    companion object {
        /**
         * Parse [ApiConfig.ai] (a JSON string) and resolve the effective base URL
         * + token for [agentKey]. Mirrors nexilcolab `getAiConfig()`:
         *  - look up `users.<agentKey>` (string token OR object {token, apiBase}),
         *    else fall back to `default`.
         *  - apiBase: entry.apiBase -> entry.baseUrl -> default.apiBase -> default.baseUrl.
         *  - phone: user.phone -> default.phone (must be true to show AI on the app).
         * Returns null when not configured (no token / no base).
         */
        fun resolve(aiJson: String?, agentKey: String?): AiDraftConfig? {
            if (aiJson.isNullOrBlank() || aiJson == "{}") return null
            val root = runCatching {
                getJsonWithConfig().parseToJsonElement(aiJson) as? JsonObject
            }.getOrNull() ?: return null

            val default = root["default"] as? JsonObject
            val users = root["users"] as? JsonObject
            val userEntry = if (!agentKey.isNullOrBlank()) users?.get(agentKey) else null

            fun str(obj: JsonObject?, key: String): String? =
                (obj?.get(key) as? JsonPrimitive)?.contentOrNull?.takeIf { it.isNotBlank() }

            fun bool(obj: JsonObject?, key: String): Boolean? {
                val p = obj?.get(key) as? JsonPrimitive ?: return null
                return when {
                    p.isString -> p.contentOrNull.equals("true", ignoreCase = true) || p.contentOrNull == "1"
                    else -> p.contentOrNull?.toBooleanStrictOrNull()
                }
            }

            // token: user entry (string or object.token) -> default.token
            val token: String? = when (userEntry) {
                is JsonPrimitive -> userEntry.contentOrNull?.takeIf { it.isNotBlank() }
                is JsonObject -> str(userEntry, "token") ?: str(default, "token")
                else -> str(default, "token")
            }
            // apiBase: user object.apiBase/baseUrl -> default.apiBase/baseUrl
            val userObj = userEntry as? JsonObject
            val apiBase: String? = str(userObj, "apiBase") ?: str(userObj, "baseUrl")
                ?: str(default, "apiBase") ?: str(default, "baseUrl")
            val phone = bool(userObj, "phone") ?: bool(default, "phone") ?: false

            if (token.isNullOrBlank() || apiBase.isNullOrBlank()) return null
            return AiDraftConfig(apiBase.trimEnd('/'), token, phone)
        }

        /** True when AI is configured for this agent AND `phone: true` in config. */
        fun visibleOnPhone(aiJson: String?, agentKey: String?): Boolean {
            val cfg = resolve(aiJson, agentKey) ?: return false
            return cfg.phone
        }
    }
}

/** One row from GET /v1/drafts (summary projection). */
@Serializable
data class AiDraftSummary(
    val id: String,
    val public_no: Long? = null,
    val status: String = "",
    val source: String? = null,
    val input_kind: String? = null,
    val process: String? = null,
    val entity_role: String? = null,
    val hint_entity_id: String? = null,
    val hint_entity_name: String? = null,
    val agent_id: Int? = null,
    val agent_name: String? = null,
    val agent_display_name: String? = null,
    val item_count: Int = 0,
    /** Active/delivery date the ERP doc will carry (server-computed). */
    val date: String? = null,
    val created_at: String? = null,
    val updated_at: String? = null,
    val approved_doc_id: String? = null,
    val approved_doc_type: String? = null,
    val approved_at: String? = null,
    val totals: JsonObject? = null,
    val confidence: AiDraftConfidence? = null,
    val warnings: JsonArray? = null,
    val resolve_warnings: JsonArray? = null,
    val error_message: String? = null,
    val active: Boolean = true,
) {
    /** A completed (approved) draft is read-only: no edits, no delete. */
    fun isApproved(): Boolean = status == "approved"

    fun isInventory(): Boolean = entity_role != "supplier" && hint_entity_id == "-1"

    /** Card title: inventory drafts prefer the counting agent over ERP "מלאי". */
    fun displayEntityName(): String {
        if (isInventory()) {
            return agent_display_name?.trim()?.takeIf { it.isNotEmpty() }
                ?: agent_name?.trim()?.takeIf { it.isNotEmpty() }
                ?: hint_entity_name?.trim()?.takeIf { it.isNotEmpty() }
                ?: "מלאי"
        }
        return hint_entity_name?.trim().orEmpty()
    }
}

/** Full draft from GET /v1/drafts/{id}. `resolved`/`edited` kept raw for flexibility. */
@Serializable
data class AiDraftFull(
    val id: String,
    val public_no: Long? = null,
    val status: String = "",
    val source: String? = null,
    val input_kind: String? = null,
    val process: String? = null,
    val entity_role: String? = null,
    val hint_entity_id: String? = null,
    val hint_entity_name: String? = null,
    val approved_doc_id: String? = null,
    val approved_doc_type: String? = null,
    val approved_at: String? = null,
    /** `trust_auto`: created by itself for a client the user approved twice
     *  with no change; `undo_allowed` / `undone` drive the בטל button. */
    val approved_meta: JsonObject? = null,
    val error_message: String? = null,
    val text_input: String? = null,
    val resolved: JsonObject? = null,
    val extracted: JsonObject? = null,
    val edited: JsonObject? = null,
    val totals: JsonObject? = null,
    val warnings: JsonArray? = null,
    val resolve_warnings: JsonArray? = null,
    /** Effective ERP date computed by GET /v1/drafts/{id}. */
    val date: String? = null,
    val created_at: String? = null,
    val updated_at: String? = null,
) {
    /** A completed (approved) draft is read-only: no edits, no delete. */
    fun isApproved(): Boolean = status == "approved"

    private fun metaFlag(key: String): Boolean =
        (approved_meta?.get(key) as? JsonPrimitive)?.contentOrNull == "true"

    /** Created without a tap by the per-client trust. */
    fun isTrustAuto(): Boolean = isApproved() && metaFlag("trust_auto")

    fun isUndone(): Boolean = metaFlag("undone")

    /** בטל is offered: trust-created, undoable, not undone yet. The server
     *  enforces the 15-minute window. */
    fun canUndo(): Boolean = isTrustAuto() && metaFlag("undo_allowed") && !isUndone()

    /** Lines with the edited overlay applied (a product picked in the compare
     *  counts as matched; a deleted line is gone). */
    fun effectiveProducts(): List<AiResolvedProduct> {
        val overlay = edited?.get("products") as? JsonObject
        return resolvedProducts().mapNotNull { p ->
            val patch = overlay?.get(p.lineId) as? JsonObject ?: return@mapNotNull p
            if ((patch["_deleted"] as? JsonPrimitive)?.contentOrNull == "true") return@mapNotNull null
            val pid = (patch["productId"] as? JsonPrimitive)?.contentOrNull?.toIntOrNull()
            val name = (patch["productName"] as? JsonPrimitive)?.contentOrNull
            val amount = (patch["amount"] as? JsonPrimitive)?.contentOrNull?.toDoubleOrNull()
            p.copy(
                productId = pid ?: p.productId,
                matchProductName = name ?: p.matchProductName,
                amount = amount ?: p.amount,
            )
        }
    }

    fun resolvedProducts(): List<AiResolvedProduct> {
        val arr = (resolved?.get("products") as? JsonArray)
            ?: (extracted?.get("products") as? JsonArray)
            ?: return emptyList()
        return arr.mapNotNull {
            runCatching {
                getJsonWithConfig().decodeFromJsonElement(AiResolvedProduct.serializer(), it)
            }.getOrNull()
        }
    }

    fun entityDisplayName(): String? {
        inventoryAgentDisplayName()?.let { return it }
        val ent = resolved?.get("entity") as? JsonObject
        val name = when (val nameEl = ent?.get("name")) {
            is JsonObject -> (nameEl["value"] as? JsonPrimitive)?.contentOrNull
            is JsonPrimitive -> nameEl.contentOrNull
            else -> null
        }
        return name?.takeIf { it.isNotBlank() }
            ?: hint_entity_name
            ?: siteCodeFromSubject()
    }

    fun entityId(): String? {
        val ent = resolved?.get("entity") as? JsonObject
        return (ent?.get("id") as? JsonPrimitive)?.contentOrNull ?: hint_entity_id
    }

    fun docType(): String? {
        val dt = resolved?.get("doc_type") as? JsonObject ?: return process
        val canonical = (dt["resolved"] as? JsonObject)?.get("canonical") as? JsonPrimitive
        val value = dt["value"] as? JsonPrimitive
        return canonical?.contentOrNull ?: value?.contentOrNull ?: process
    }

    fun docDate(): String? {
        if (!date.isNullOrBlank()) return date
        val meta = (edited?.get("meta") as? JsonObject)
        val edl = (meta?.get("date") as? JsonPrimitive)?.contentOrNull
        if (!edl.isNullOrBlank()) return edl
        val dl = resolved?.get("date")
        return when (dl) {
            is JsonObject -> (dl["value"] as? JsonPrimitive)?.contentOrNull
            is JsonPrimitive -> dl.contentOrNull
            else -> null
        }
    }

    fun confidence(): AiDraftConfidence? {
        val c = resolved?.get("confidence") as? JsonObject ?: return null
        return runCatching {
            getJsonWithConfig().decodeFromJsonElement(AiDraftConfidence.serializer(), c)
        }.getOrNull()
    }

    /** Resolved products with the `edited.products` overlay (amount / chosen product
     *  / unit) folded on top — mirrors the web compare's display merge. Brand-new
     *  overlay-only lines (added by the user in the compare UI) are appended;
     *  lines tombstoned with `_deleted: true` are dropped (same as the web). */
    fun mergedProducts(): List<AiResolvedProduct> {
        val overlay = edited?.get("products") as? JsonObject
        fun isDeleted(ov: JsonObject?): Boolean =
            (ov?.get("_deleted") as? JsonPrimitive)?.contentOrNull
                .let { it == "true" || it == "1" }
        val base = resolvedProducts()
        val merged = base.mapNotNull { p ->
            val ov = overlay?.get(p.lineId) as? JsonObject ?: return@mapNotNull p
            if (isDeleted(ov)) return@mapNotNull null
            val newAmount = (ov["amount"] as? JsonPrimitive)?.doubleOrNull
            val pricePatched = ov.containsKey("price")
            val newProduct = (ov["productId"] as? JsonPrimitive)?.intOrNull
            // A swapped product: the old product's NihulKav price no longer applies.
            val swapped = newProduct != null && newProduct != p.productId
            p.copy(
                amount = newAmount ?: p.amount,
                resolvedAmount = if (newAmount != null) null else p.resolvedAmount,
                price = if (pricePatched) (ov["price"] as? JsonPrimitive)?.doubleOrNull else p.price,
                resolvedPrice = if (pricePatched) null else p.resolvedPrice,
                erpPrice = if (swapped) (ov["erpPrice"] as? JsonPrimitive)?.doubleOrNull else p.erpPrice,
                entityPrice = if (swapped) null else p.entityPrice,
                matchPrice = if (swapped) null else p.matchPrice,
                secondAmount = if (ov.containsKey("secondAmount")) {
                    (ov["secondAmount"] as? JsonPrimitive)?.doubleOrNull
                } else p.secondAmount,
                secondUnit = (ov["secondUnit"] as? JsonPrimitive)?.contentOrNull ?: p.secondUnit,
                thirdAmount = if (ov.containsKey("thirdAmount")) {
                    (ov["thirdAmount"] as? JsonPrimitive)?.doubleOrNull
                } else p.thirdAmount,
                productId = newProduct ?: p.productId,
                matchProductName = (ov["matchProductName"] as? JsonPrimitive)?.contentOrNull
                    ?: (ov["productName"] as? JsonPrimitive)?.contentOrNull ?: p.matchProductName,
                matchUnit = (ov["unit"] as? JsonPrimitive)?.contentOrNull ?: p.matchUnit,
                // A manual product pick writes matchConfidence=1.0 +
                // matchSource="manual" into the overlay; without folding them
                // the row keeps flagging the ORIGINAL match's low confidence
                // even after the user explicitly chose the product.
                matchConfidence = (ov["matchConfidence"] as? JsonPrimitive)?.doubleOrNull
                    ?: p.matchConfidence,
                matchSource = (ov["matchSource"] as? JsonPrimitive)?.contentOrNull ?: p.matchSource,
                matchDays = overlayStringList(ov, "days", "matchDays") ?: p.matchDays,
                matchDayAvailable = overlayBool(ov, "dayAvailable", "matchDayAvailable")
                    ?: p.matchDayAvailable,
                matchAvailable = overlayBool(ov, "available", "matchAvailable") ?: p.matchAvailable,
            )
        }
        if (overlay == null) return merged
        val known = base.map { it.lineId }.toSet()
        val extra = overlay.entries
            .filter { it.key !in known }
            .mapNotNull { (lineId, el) ->
                val ov = el as? JsonObject ?: return@mapNotNull null
                if (isDeleted(ov)) return@mapNotNull null
                AiResolvedProduct(
                    lineId = lineId,
                    productId = (ov["productId"] as? JsonPrimitive)?.intOrNull,
                    productName = (ov["productName"] as? JsonPrimitive)?.contentOrNull,
                    matchProductName = (ov["matchProductName"] as? JsonPrimitive)?.contentOrNull
                        ?: (ov["productName"] as? JsonPrimitive)?.contentOrNull,
                    amount = (ov["amount"] as? JsonPrimitive)?.doubleOrNull,
                    price = (ov["price"] as? JsonPrimitive)?.doubleOrNull,
                    secondAmount = (ov["secondAmount"] as? JsonPrimitive)?.doubleOrNull,
                    secondUnit = (ov["secondUnit"] as? JsonPrimitive)?.contentOrNull,
                    matchUnit = (ov["unit"] as? JsonPrimitive)?.contentOrNull,
                    matchConfidence = (ov["matchConfidence"] as? JsonPrimitive)?.doubleOrNull,
                    matchSource = (ov["matchSource"] as? JsonPrimitive)?.contentOrNull,
                    matchDays = overlayStringList(ov, "days", "matchDays") ?: emptyList(),
                    matchDayAvailable = overlayBool(ov, "dayAvailable", "matchDayAvailable"),
                    matchAvailable = overlayBool(ov, "available", "matchAvailable"),
                    rawText = (ov["rawText"] as? JsonPrimitive)?.contentOrNull,
                )
            }
        return merged + extra
    }

    private fun overlayStringList(ov: JsonObject, vararg keys: String): List<String>? {
        val el = keys.firstNotNullOfOrNull { ov[it] } ?: return null
        val arr = el as? JsonArray ?: return emptyList()
        return arr.mapNotNull { (it as? JsonPrimitive)?.contentOrNull?.trim()?.takeIf { s -> s.isNotEmpty() } }
    }

    private fun overlayBool(ov: JsonObject, vararg keys: String): Boolean? {
        val p = keys.firstNotNullOfOrNull { ov[it] } as? JsonPrimitive ?: return null
        return p.booleanOrNull ?: when (p.contentOrNull?.lowercase()) {
            "true", "1" -> true
            "false", "0" -> false
            else -> null
        }
    }

    private fun totalNum(k: String): Double? = (totals?.get(k) as? JsonPrimitive)?.doubleOrNull
    fun totalNet(): Double? = totalNum("net")
    fun totalVat(): Double? = totalNum("vat")
    fun totalGross(): Double? = totalNum("gross")

    fun editedMeta(): JsonObject? = edited?.get("meta") as? JsonObject

    fun inventoryAgentDisplayName(): String? {
        if (entity_role == "supplier" || hint_entity_id != "-1") return null
        val meta = editedMeta() ?: return null
        fun metaText(key: String): String? =
            (meta[key] as? JsonPrimitive)?.contentOrNull?.trim()?.takeIf { it.isNotBlank() }
        return metaText("agentDisplayName") ?: metaText("agentName")
    }

    /**
     * Inventory drafts pin an ERP agent (`edited.meta.agentId` + login/display
     * name). Compare UIs should show the name, not only `#id`.
     */
    fun inventoryAgentLabel(): String? {
        val meta = editedMeta() ?: return null
        fun metaText(key: String): String? =
            (meta[key] as? JsonPrimitive)?.contentOrNull?.trim()?.takeIf { it.isNotBlank() }
        fun metaInt(key: String): Int? {
            val p = meta[key] as? JsonPrimitive ?: return null
            return p.intOrNull ?: p.contentOrNull?.toIntOrNull()
        }
        val id = metaInt("agentId")?.takeIf { it > 0 }
        val display = metaText("agentDisplayName") ?: metaText("agentName")
        val login = metaText("agentName")
        return when {
            display != null && id != null && login != null && login != display ->
                "סוכן $display ($login #$id)"
            display != null && id != null -> "סוכן $display (#$id)"
            display != null -> "סוכן $display"
            id != null -> "סוכן #$id"
            else -> null
        }
    }

    /** Email subject stored at ingest (sourceMsg, else the identity chain). */
    fun sourceSubject(): String? {
        val meta = editedMeta() ?: return null
        val sm = meta["sourceMsg"] as? JsonObject
        val chainMsg = (meta["identityChain"] as? JsonObject)?.get("msg") as? JsonObject
        return ((sm?.get("subject") as? JsonPrimitive)?.contentOrNull
            ?: (chainMsg?.get("subject") as? JsonPrimitive)?.contentOrNull)
            ?.trim()?.takeIf { it.isNotBlank() }
    }

    fun xlsxText(): String? =
        (editedMeta()?.get("xlsxText") as? JsonPrimitive)?.contentOrNull
            ?.trim()?.takeIf { it.isNotBlank() }

    /**
     * SITE_CODE from a Wolt Market PO subject
     * (`… DD/MM/YYYY - WM_WOLFSON - 1912006137` → `WM_WOLFSON`).
     */
    fun siteCodeFromSubject(): String? {
        val subject = sourceSubject() ?: return null
        val re = Regex(
            """wolt\s+market\s+purchase\s+order\s*:\s*\d{1,2}[./-]\d{1,2}[./-]\d{2,4}\s*-\s*([A-Za-z][A-Za-z0-9_]{1,40})\s*-\s*\d+""",
            RegexOption.IGNORE_CASE,
        )
        return re.find(subject)?.groupValues?.getOrNull(1)
    }

    /** Text shown on the compare source side: subject + xlsx flatten, else body. */
    fun compareSourceText(): String? {
        val subject = sourceSubject()
        val xlsx = xlsxText()
        val body = text_input?.trim()?.takeIf { it.isNotBlank() }
        val parts = buildList {
            if (!subject.isNullOrBlank()) add("נושא: $subject")
            if (!xlsx.isNullOrBlank()) add(xlsx)
            else if (!body.isNullOrBlank()) add(body)
        }
        return parts.joinToString("\n\n").ifBlank { null }
    }
}

@Serializable
data class AiResolvedProduct(
    val lineId: String = "",
    val productName: String? = null,
    val rawText: String? = null,
    val amount: Double? = null,
    val unit: String? = null,
    val price: Double? = null,
    val total: Double? = null,
    val barcode: String? = null,
    val extId: String? = null,
    val productId: Int? = null,
    val matchProductName: String? = null,
    val matchSource: String? = null,
    val matchConfidence: Double? = null,
    val matchReason: String? = null,
    val matchAvailable: Boolean? = null,
    val matchDays: List<String> = emptyList(),
    val matchDayAvailable: Boolean? = null,
    val matchUnit: String? = null,
    /** The matched ERP product's own unit labels — the unit picker must offer
     *  THESE (not the global WrappedName list) so the chosen spelling always
     *  matches the ERP product. */
    val matchAvailableUnits: List<String> = emptyList(),
    /** The resolver's ranked alternatives for this line (same list the web
     *  swap-autocomplete shows first). Empty for memory/exact matches. */
    val candidates: List<AiProductCandidate> = emptyList(),
    val resolvedAmount: Double? = null,
    val erpPrice: Double? = null,
    val _amountMemoryConverted: Boolean? = null,
    /** The resolver's unit price (wins over [price] until the reviewer edits it). */
    val resolvedPrice: Double? = null,
    val entityPrice: Double? = null,
    val matchPrice: Double? = null,
    /** Second / third printed quantities (packages, pallets) → ERP `wv`. */
    val secondAmount: Double? = null,
    val secondUnit: String? = null,
    val thirdAmount: Double? = null,
    val thirdUnit: String? = null,
    val matchSecondaryUnit: String? = null,
    val matchThirdUnit: String? = null,
) {
    /** Units to offer when editing this line: the matched product's own list,
     *  falling back to the picked candidate's list. Empty = caller falls back
     *  to the global WrappedName list. */
    fun unitsForPick(): List<String> {
        if (matchAvailableUnits.isNotEmpty()) return matchAvailableUnits
        val cand = candidates.firstOrNull { it.id != null && it.id == productId }
        return cand?.availableUnits ?: emptyList()
    }

    /** The unit price printed on the document (or the reviewer's edit); null when none. */
    fun docPrice(): Double? = (resolvedPrice ?: price)?.takeIf { it != 0.0 }

    /** The matched product's NihulKav price (supplier price list, else catalog). */
    fun nihulKavPrice(): Double? =
        if (productId == null) null else (erpPrice ?: entityPrice ?: matchPrice)?.takeIf { it != 0.0 }

    /** Document and NihulKav prices both known and more than an agora apart. */
    fun hasPriceGap(): Boolean {
        val doc = docPrice() ?: return false
        val nk = nihulKavPrice() ?: return false
        return kotlin.math.abs(doc - nk) > 0.01
    }

    /** price × charged amount, else the printed line total. */
    fun lineTotal(): Double? {
        val p = docPrice()
        val a = resolvedAmount ?: amount
        if (p != null && a != null) return kotlin.math.round(p * a * 100) / 100
        return total
    }

    /** "30 קרטון · 2 משטח" — the second / third printed quantities. */
    fun extraQuantitiesText(): String {
        fun fmt(v: Double): String = if (v % 1.0 == 0.0) v.toLong().toString() else v.toString()
        val second = secondAmount?.takeIf { it > 0 }?.let { a ->
            val u = (secondUnit ?: matchSecondaryUnit)?.takeIf { it.isNotBlank() }
            if (u != null) "${fmt(a)} $u" else fmt(a)
        }
        val third = thirdAmount?.takeIf { it > 0 }?.let { a ->
            val u = (thirdUnit ?: matchThirdUnit)?.takeIf { it.isNotBlank() }
            if (u != null) "${fmt(a)} $u" else fmt(a)
        }
        return listOfNotNull(second, third).joinToString(" · ")
    }
}

/** One swap candidate on a resolved product line (compact resolver output). */
@Serializable
data class AiProductCandidate(
    val id: Int? = null,
    val name: String? = null,
    val availableUnits: List<String> = emptyList(),
    val unit: String? = null,
    val barcode: String? = null,
    val extId: String? = null,
    val price: Double? = null,
    val entityPrice: Double? = null,
    val available: Boolean? = null,
    val days: List<String> = emptyList(),
    val dayAvailable: Boolean? = null,
    val prevOrdered: Int? = null,
    val timesOrdered: Int? = null,
    val memoryHint: Double? = null,
    /** Resolver fuzzy/match confidence 0..1 (packed on each candidate). */
    val score: Double? = null,
)

@Serializable
data class AiDraftConfidence(
    val score: Double = 0.0,
    val entity: Double = 0.0,
    val entity_matched: Boolean = false,
    val products_avg: Double = 0.0,
    val products_min: Double? = null,
    val matched: Int = 0,
    val total: Int = 0,
    val all_matched: Boolean = false,
)

@Serializable
data class AiDraftFileInput(
    val filename: String,
    val mime_type: String,
    val base64: String,
)

/** Body for POST /v1/drafts. */
@Serializable
data class AiDraftCreateRequest(
    val source: String = "drawer",
    val process: String? = null,
    val entity_role: String? = null,
    val hint_entity_id: String? = null,
    val hint_entity_name: String? = null,
    /** How the entity hint was chosen. `manual`/`fixed` lock an ERP id. */
    val identity_source: String? = null,
    val text: String? = null,
    val files: List<AiDraftFileInput> = emptyList(),
    val auto_run: Boolean = true,
    val date_strategy: String? = null,
    val date: String? = null,
)

/** Body for POST /v1/drafts/{draftId}/supplier-products. */
@Serializable
data class AiSupplierProductCreateRequest(
    val line_id: String,
    val name: String? = null,
    val barcode: String? = null,
    val external_id: String? = null,
    val connected_product_id: Int? = null,
    val price: Double? = null,
    val date: String? = null,
)

/** Result of creating/reusing a supplier product for an unmatched draft row. */
@Serializable
data class AiSupplierProductCreateResult(
    val supplier_product_id: Int,
    /** Backend adapters may return either the linked id or a compact product object. */
    val connected_product: JsonElement? = null,
    val price_row_created: Boolean = false,
    val reused: Boolean = false,
    val draft: AiDraftFull? = null,
)

/** One existing ERP order returned in a 409 order_conflict (same client +
 *  delivery date, or same source ref). Mirrors the web OrderConflictCandidate. */
@Serializable
data class AiOrderConflictCandidate(
    val order_id: Int,
    val active: Int = 0,
    val status_label: String = "",
    val date: String? = null,
    val item_count: Int = 0,
    val is_empty: Boolean = false,
    val is_locked: Boolean = false,
    val ref_id: String? = null,
    val ref_match: Boolean = false,
    val notes: String = "",
)

/** Parsed body of a 409 order_conflict from POST /v1/drafts/{id}/approve. */
@Serializable
data class AiOrderConflict(
    val message: String? = null,
    val candidates: List<AiOrderConflictCandidate> = emptyList(),
)

@Serializable
data class AiApproveResult(
    val draft_id: String? = null,
    val public_no: Long? = null,
    val doc_id: String? = null,
    val doc_type: String? = null,
    val approved_at: String? = null,
)

/** One line an approve question names: a line with no price (missing_price),
 *  a doubtful quantity (unit_check: flags + note), or a supplier price that
 *  differs from NihulKav (price_diff: prices before VAT). */
@Serializable
data class AiApproveQuestionLine(
    val line_id: String = "",
    val name: String = "",
    val flags: List<String> = emptyList(),
    val note: String? = null,
    val product_id: Int? = null,
    val amount: Double? = null,
    val doc_price: Double? = null,
    val erp_price: Double? = null,
)

/** A supplier's printed total vs the ERP's quote of the lines (total_mismatch). */
@Serializable
data class AiTotalGap(
    val printed: Double = 0.0,
    val computed: Double = 0.0,
    /** printed − computed: the rounding line's quantity at ₪1. */
    val diff: Double = 0.0,
    val basis: String = "net",
)

/** A 409 from POST /v1/drafts/{id}/approve that a person answers before the
 *  document is created: `missing_price`, `unit_check`, `total_mismatch` or
 *  `price_diff`. Approve again with the answer in [AiApproveAnswers]. */
@Serializable
data class AiApproveQuestion(
    val error: String = "",
    val message: String? = null,
    val lines: List<AiApproveQuestionLine> = emptyList(),
    /** missing_price: "tax_note" (no price on the line) or "client_price" (not in the price list). */
    val kind: String? = null,
    val gap: AiTotalGap? = null,
    /** price_diff: the document date — the default first day of updated supplier prices. */
    val date: String? = null,
    /** price_diff: which price the document carries, "doc" or "erp". */
    val posting: String? = null,
) {
    companion object {
        const val MISSING_PRICE = "missing_price"
        const val UNIT_CHECK = "unit_check"
        const val TOTAL_MISMATCH = "total_mismatch"
        const val PRICE_DIFF = "price_diff"
        val CODES = setOf(MISSING_PRICE, UNIT_CHECK, TOTAL_MISMATCH, PRICE_DIFF)
    }
}

/** The answers given so far in one approve. Each question adds its answer and
 *  approve is called again with all of them (a later question must not undo
 *  an earlier answer). */
data class AiApproveAnswers(
    /** missing_price: "keep" or "drop". */
    val missingPrice: String? = null,
    val acceptUnitWarnings: Boolean = false,
    /** total_mismatch: "keep" or "adjust". */
    val totalChoice: String? = null,
    val priceDiffAck: Boolean = false,
    /** price_diff lines whose document price goes into the supplier price list. */
    val updateSupplierPrices: List<String> = emptyList(),
    /** First date (YYYY-MM-DD) of the updated supplier prices; null = the document date. */
    val priceFrom: String? = null,
) {
    fun queryParams(): List<String> = buildList {
        missingPrice?.takeIf { it == "keep" || it == "drop" }?.let { add("missing_price=$it") }
        if (acceptUnitWarnings) add("accept_unit_warnings=true")
        totalChoice?.takeIf { it == "keep" || it == "adjust" }?.let { add("total_choice=$it") }
        if (priceDiffAck) {
            add("price_diff_ack=true")
            if (updateSupplierPrices.isNotEmpty()) {
                add("update_supplier_prices=" + updateSupplierPrices.joinToString(",").encodeURLParameter())
                priceFrom?.takeIf { it.isNotBlank() }?.let { add("price_from=$it") }
            }
        }
    }
}
